import { useEffect, useRef, useState } from 'react'

declare global {
    interface Window {
        webkitSpeechRecognition: any
        SpeechRecognition: any
    }
}

interface UseSpeechToTextOptions {
    continuous?: boolean
    lang?: string
}

export const useSpeechToText = ({ continuous = false, lang = 'en-US' }: UseSpeechToTextOptions = {}) => {
    const recognitionRef = useRef<any>(null)
    const [isListening, setIsListening] = useState(false)
    const [transcript, setTranscript] = useState('')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const SpeechRecognition = globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition

        if (!SpeechRecognition) {
            setError('Speech recognition is not supported in this browser.')
            return
        }

        const recognition = new SpeechRecognition()
        recognition.lang = lang
        recognition.continuous = continuous
        recognition.interimResults = true

        recognition.onstart = () => setIsListening(true)

        recognition.onend = () => setIsListening(false)

        recognition.onerror = (event: any) => {
            setError(event.error)
        }

        recognition.onresult = (event: any) => {
            let finalTranscript = ''
            let interimTranscript = ''

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]
                if (result.isFinal) {
                    finalTranscript += result[0].transcript
                } else {
                    interimTranscript += result[0].transcript
                }
            }

            if (finalTranscript) {
                setTranscript((prev) => {
                    const idx = prev.indexOf('\u200B')
                    const base = idx === -1 ? prev : prev.substring(0, idx)
                    return base + finalTranscript
                })
            } else if (interimTranscript) {
                setTranscript((prev) => {
                    const idx = prev.indexOf('\u200B')
                    const base = idx === -1 ? prev : prev.substring(0, idx)
                    return base + '\u200B' + interimTranscript
                })
            }
        }

        recognitionRef.current = recognition
    }, [continuous, lang])

    const startListening = () => recognitionRef.current?.start()
    const stopListening = () => recognitionRef.current?.stop()
    const resetTranscript = () => setTranscript('')

    return {
        transcript,
        isListening,
        error,
        startListening,
        stopListening,
        resetTranscript,
        setTranscript,
    }
}
